import { type AnyElement, type AnyElementData } from '../all-element-types';
import {
  type ElementData,
  type Element,
  elementType,
  type ElementType,
} from '../element';

import { type ConnectorElementData } from './connector';

/**
 * Represents the 'type' of a smart connector.
 */
export enum RelationKind {
  /**
   * @description A ISSUE_HIERARCHY type infers that the connector is between
   * a parent and child issue. The {@link RelationDirection} of the connector will accordingly
   * point towards the child.
   * @example A Jira epic & Jira issue.
   */
  ISSUE_HIERARCHY = 1,
  /**
   * @description A link type infers the connector is between two issues
   * which exist at the same level. I.e., there is no hierarchy between them.
   * @example a Jira issue & Jira issue
   */
  ISSUE_LINK = 2,
}

/**
 * Represents the direction a smart connector goes in.
 * @description Consider the case of a Jira issue which blocks another Jira
 * issue. This can be represented using either 'blocks' or 'blocked by'.
 */
export enum RelationDirection {
  /**
   * @description Direction is in direction of {@link SmartConnectorElement.sourceElement}. I.e, ends at source.
   */
  TOWARDS_SOURCE = 1,
  /**
   * @description Direction is in direction of {@link SmartConnectorElement.targetElement}. I.e, ends at target.
   */
  TOWARDS_TARGET = 2,
}

export interface SmartConnectorElementData
  extends Omit<ConnectorElementData, 'type'>,
    ElementData<ElementType> {
  /**
   * The kind of relation this smart connector represents.
   */
  kind: RelationKind;
  /**
   * The direction this relation goes in.
   * @example From parent -> child
   * @example From ISSUE_LINK -> ISSUE_LINK or ISSUE_LINK <- ISSUE_LINK.
   */
  direction: RelationDirection;
  /**
   * The external id that this relationship refers to
   * @example Jira issue link ID
   */
  relationshipId: string;
  /**
   * The proportion of the connector's line that the label should be displayed at.
   */
  proportion: number;
}

export interface SmartConnectorElementPasteData extends SmartConnectorElementData {
  /**
   * The index of the connector's source element in the array of copied elements.
   */
  sourceIndex: number;
  /**
   * The index of the connector's target element in the array of copied elements.
   */
  targetIndex: number;
}

export interface SmartConnectorElement
  extends SmartConnectorElementData,
    Element<ElementType> {}

export function isSmartConnector(
  element: AnyElement | undefined
): element is SmartConnectorElement {
  return element?.type === smartConnectorElementType;
}

export function isSmartConnectorData(
  element: AnyElementData
): element is SmartConnectorElementData {
  return element?.type === smartConnectorElementType;
}

export const smartConnectorElementType = elementType('smartConnector');
